import { buildPoolProductKey } from './presettlement-product-key.ts';

export type TariffWeek = 'general' | 'reception' | 'process' | 'dispatch';
export interface TariffConfiguration {
  version: 1;
  packing: 'general' | 'packing';
  packaging: 'format' | 'code';
  week: TariffWeek;
  defaultRate: number;
  rates: Record<string, number>;
}
export interface TariffBox {
  packing_name?: string | null;
  box_type?: string | null;
  box_format?: string | null;
  complete_format?: string | null;
  box_weight?: number | null;
  unit_weight?: number | null;
  quantity?: number | null;
  reception_date?: string | null;
  process_date?: string | null;
  packing_etd?: string | null;
}
export const TARIFF_WEEK_LABELS: Record<TariffWeek, string> = {
  general: 'General',
  reception: 'Semana Recepción',
  process: 'Semana Proceso',
  dispatch: 'Semana Despacho Packing',
};
export const createTariffConfiguration = (): TariffConfiguration => ({
  version: 1,
  packing: 'general',
  packaging: 'format',
  week: 'general',
  defaultRate: 2,
  rates: {},
});
const token = (value: string | null | undefined) =>
  value?.trim().toUpperCase() || '';
const validRate = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

export function parseTariffConfiguration(
  value: unknown
): TariffConfiguration | null {
  if (!value || typeof value !== 'object') return null;
  const config = value as TariffConfiguration;
  if (
    config.version !== 1 ||
    !['general', 'packing'].includes(config.packing) ||
    !['format', 'code'].includes(config.packaging) ||
    !['general', 'reception', 'process', 'dispatch'].includes(config.week) ||
    !validRate(config.defaultRate) ||
    !config.rates ||
    typeof config.rates !== 'object' ||
    Array.isArray(config.rates) ||
    !Object.values(config.rates).every(validRate)
  )
    return null;
  return { ...config, rates: { ...config.rates } };
}

// ISO week of the source calendar date, independent of server/browser timezone.
export function tariffWeek(date: string | null | undefined): string | null {
  const match = date?.match(/^(\d{4})-(\d{2})-(\d{2})(?:T|$)/);
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number);
  const value = new Date(Date.UTC(year, month - 1, day));
  if (
    value.getUTCFullYear() !== year ||
    value.getUTCMonth() !== month - 1 ||
    value.getUTCDate() !== day
  )
    return null;
  value.setUTCDate(value.getUTCDate() + 4 - (value.getUTCDay() || 7));
  const weekYear = value.getUTCFullYear();
  const week = Math.ceil(
    ((value.getTime() - Date.UTC(weekYear, 0, 1)) / 86400000 + 1) / 7
  );
  return `${weekYear}-W${String(week).padStart(2, '0')}`;
}

export function tariffDimensions(box: TariffBox, config: TariffConfiguration) {
  const packing =
    config.packing === 'packing' ? token(box.packing_name) || null : null;
  const packaging =
    config.packaging === 'code'
      ? token(box.box_type) || null
      : box.box_format || box.complete_format
        ? buildPoolProductKey(box, ['box_format'])
        : null;
  const date =
    config.week === 'reception'
      ? box.reception_date
      : config.week === 'process'
        ? box.process_date
        : box.packing_etd;
  const week = config.week === 'general' ? null : tariffWeek(date);
  return { packing, packaging, week };
}
export function tariffKey(box: TariffBox, config: TariffConfiguration): string {
  const { packing, packaging, week } = tariffDimensions(box, config);
  return JSON.stringify([
    config.packing,
    packing,
    config.packaging,
    packaging,
    config.week,
    week,
  ]);
}
export function resolveTariff(box: TariffBox, config: TariffConfiguration) {
  const key = tariffKey(box, config);
  const specific = config.rates[key];
  return {
    key,
    rate: validRate(specific) ? specific : config.defaultRate,
    source: validRate(specific) ? ('specific' as const) : ('general' as const),
  };
}
export type TariffApplication = 'sameCost' | 'formatCost';

// Build once from the full season/species shipment base, before display filters.
export function createAppliedTariffResolver(
  boxes: TariffBox[],
  config: TariffConfiguration,
  application: TariffApplication
) {
  let kilos = 0;
  let total = 0;
  if (application === 'sameCost') {
    for (const box of boxes) {
      const weight = Number(box.box_weight ?? box.unit_weight ?? 0) *
        Number(box.quantity ?? 0);
      if (!Number.isFinite(weight) || weight <= 0) continue;
      kilos += weight;
      total += weight * resolveTariff(box, config).rate;
    }
  }
  const average = kilos > 0 ? total / kilos : config.defaultRate;
  return (box: TariffBox) => {
    const configured = resolveTariff(box, config);
    return application === 'sameCost'
      ? { ...configured, rate: average, source: 'weighted-average' as const }
      : configured;
  };
}

export function legacyTariffConfiguration(
  costs: Record<string, number>
): TariffConfiguration {
  const config = createTariffConfiguration();
  for (const [format, rate] of Object.entries(costs)) {
    if (format === 'average' || !validRate(rate)) continue;
    config.rates[tariffKey({ complete_format: format }, config)] = rate;
  }
  return config;
}
export function tariffRows(boxes: TariffBox[], config: TariffConfiguration) {
  const rows = new Map<
    string,
    ReturnType<typeof tariffDimensions> & {
      key: string;
      kilos: number;
      count: number;
    }
  >();
  for (const box of boxes) {
    const key = tariffKey(box, config);
    const row = rows.get(key) ?? {
      key,
      ...tariffDimensions(box, config),
      kilos: 0,
      count: 0,
    };
    row.kilos += Math.max(
      0,
      Number(box.box_weight ?? box.unit_weight ?? 0) * Number(box.quantity ?? 0)
    );
    row.count++;
    rows.set(key, row);
  }
  return [...rows.values()].sort(
    (a, b) =>
      (a.packing ?? '').localeCompare(b.packing ?? '') ||
      (a.packaging ?? '').localeCompare(b.packaging ?? '') ||
      (a.week ?? '').localeCompare(b.week ?? '')
  );
}
